import { useCallback, useContext } from "react";

import { parseFile } from "@/hooks/UseTusHooks/Generics";
import { AppContext } from "@/models/AppStateProvider";
import { DataContext } from "@/models/DataProvider";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { fetchUploadConfig } from "@/utils/recording";
import type { ComposerUpload } from "./composerSend";
import { startComposerUpload } from "./composerUpload";

/**
 * The upload a composer's send puts its file through, read from the app's contexts: the upload service and stage,
 * the sender, and their language. It uploads the file tagged with the channel, so the upload pipeline posts it, under
 * the details the Attach popover already read or, for a pasted file, the ones `parseFile` reads now. One identity
 * while the contexts hold still, so the send that closes over it does too.
 *
 * A file that cannot be read fails the send, and the draft keeps the file for another try.
 */
export function useComposerUpload(): ComposerUpload {
  const { config } = useContext(AppContext);
  const { tusUrl, stage } = fetchUploadConfig(config);
  const { preferredLanguage } = useContext(DataContext);
  const { myAccountId } = useContext(MyAccountContext);
  return useCallback(
    async ({ attachment, feedId }) => {
      const parsed = attachment.meta
        ? { file: attachment.file, meta: attachment.meta }
        : await parseFile(attachment.file).catch(() => undefined);
      if (!parsed?.meta) {
        throw new Error("Invalid file");
      }
      return startComposerUpload(parsed, feedId, {
        tusUrl,
        stage,
        accountId: myAccountId ?? "",
        preferredLanguage: String(preferredLanguage),
      });
    },
    [tusUrl, stage, myAccountId, preferredLanguage],
  );
}
